import { GeneralApiService } from '@api_patient/general_api.service';
import { IdentityApiService } from '@api_patient/identity_api.service';
import { SessionApiService } from '@api_patient/session_api.service';
import { DateService } from '@application/time/date.service';
import { UniversalConfig } from '@application/universal_config.service';
import { ControllerProtocol, Injectable, Scopes } from '@visiba/instantiation';
import { Presentation } from '@visiba/presentation';
import { Timer } from '@visiba/std';

@Injectable({
	scope: Scopes.Scoped,
})
export class IdentitySessionController implements ControllerProtocol {
	public state = Presentation.create<StateModel>({
		needsTouch: false,
	});
	private isAliveInterval = new Timer();
	private isAliveTimerInterval = 1000 * 30;
	private beforeSignOutCallback?: () => Promise<void>;
	private readonly signOutTimer = new Timer();

	constructor(
		private readonly identityApiService: IdentityApiService,
		private readonly dateService: DateService,
		private readonly sessionApiService: SessionApiService,
		private readonly generalApiService: GeneralApiService,
		private readonly universalConfig: UniversalConfig,
	) {}

	public whenMount() {
		this.isAliveInterval.repeat(this.checkIsAlive.bind(this), this.isAliveTimerInterval);
	}

	public whenDemount() {
		this.isAliveInterval.flush();
		this.signOutTimer.flush();
	}

	public async touchSession() {
		const result = await this.generalApiService.touchSession();
		if (result.ok) {
			this.signOutTimer.flush();

			this.state.write({
				needsTouch: false,
			});
		} else if (result.err?.statusCode === 401) {
			if (this.beforeSignOutCallback) await this.beforeSignOutCallback();
			await this.signOut();
		}
	}

	/**
	 * Register a callback to be called before sign out.
	 */
	public setBeforeSignOutCallback(cb?: () => Promise<void>) {
		this.beforeSignOutCallback = cb;
	}

	public async signOut() {
		if (this.beforeSignOutCallback) {
			await this.beforeSignOutCallback();
		}

		// Create and submit sign-out form
		const form = document.createElement('form');
		form.method = 'post';
		form.action = this.identityApiService.getSignOutPath(this.universalConfig.alias);
		form.style.display = 'none';

		document.body.appendChild(form);
		form.submit();
	}

	private async checkIsAlive() {
		const result = await this.sessionApiService.getGuestSession();

		if (result.ok) {
			this.signOutTimer.flush();

			const touchExpirationDate = this.dateService.newDate(result.ok.data.TouchExpiration);
			const currentDate = this.dateService.newDate();
			const needsTouch = touchExpirationDate.subtract(5, 'minute').isBefore(currentDate);

			const timeOutMs = touchExpirationDate.diff(currentDate, 'ms');

			this.signOutTimer.wait(() => {
				this.signOut();
			}, timeOutMs);

			this.state.write({
				needsTouch,
			});
		}

		if (result.err?.statusCode === 401) {
			await this.signOut();
		}
	}
}

interface StateModel {
	needsTouch: boolean;
}
